import React, { useState, useMemo, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { hasGranularPermission } from '../lib/permissions';
import { useLocation } from 'react-router-dom';
import MetricaGeneros from './metricas/MetricaGeneros';
import MetricaRegioes from './metricas/MetricaRegioes';
import MetricaFaixasEtarias from './metricas/MetricaFaixasEtarias';
import MetricaHierarquias from './metricas/MetricaHierarquias';
import MetricaEstadoCivil from './metricas/MetricaEstadoCivil';
import MetricaDeficiencia from './metricas/MetricaDeficiencia';
import MetricaDependentes from './metricas/MetricaDependentes';
import MetricaTempoEmpresa from './metricas/MetricaTempoEmpresa';
import MetricaEscolaridade from './metricas/MetricaEscolaridade';
import MetricaPeriodoExperiencia from './metricas/MetricaPeriodoExperiencia';
import MetricaTipoContrato from './metricas/MetricaTipoContrato';
import HeadCountPage from './HeadCountPage';
import { BarChart3, PanelLeftClose, PanelLeft } from 'lucide-react';

const ALL_MENU_ITEMS = [
  { id: 'generos', label: 'Gêneros', color: 'purple', icon: '👥' },
  { id: 'regioes', label: 'Regiões', color: 'blue', icon: '🗺️' },
  { id: 'faixas_etarias', label: 'Faixas Etárias', color: 'green', icon: '📅' },
  { id: 'hierarquias', label: 'Hierarquias', color: 'orange', icon: '📊' },
  { id: 'estado_civil', label: 'Estado Civil', color: 'pink', icon: '💍' },
  { id: 'deficiencia', label: 'Deficiência', color: 'red', icon: '♿' },
  { id: 'dependentes', label: 'Dependentes', color: 'teal', icon: '👨‍👩‍👧' },
  { id: 'tempo_empresa', label: 'Tempo de Empresa', color: 'indigo', icon: '⏱️' },
  { id: 'escolaridade', label: 'Escolaridade', color: 'yellow', icon: '🎓' },
  { id: 'periodo_experiencia', label: 'Período Experiência', color: 'cyan', icon: '📝' },
  { id: 'tipo_contrato', label: 'Tipo de Contrato', color: 'lime', icon: '📄' },
  { id: 'headcount', label: 'HeadCount (KPI)', color: 'violet', icon: '💰' },
];

const MetricasPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    return tabFromUrl && ALL_MENU_ITEMS.some(t => t.id === tabFromUrl) ? tabFromUrl : 'generos';
  });

  // Sincronizar tab quando a URL mudar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab && ALL_MENU_ITEMS.some(t => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  const menuItems = useMemo(() => {
    return ALL_MENU_ITEMS.filter(item =>
      hasGranularPermission(user, 'metricas', item.id)
    );
  }, [user]);

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-500 hover:bg-purple-600',
      blue: 'bg-blue-500 hover:bg-blue-600',
      green: 'bg-green-500 hover:bg-green-600',
      orange: 'bg-orange-500 hover:bg-orange-600',
      pink: 'bg-pink-500 hover:bg-pink-600',
      red: 'bg-red-500 hover:bg-red-600',
      teal: 'bg-teal-500 hover:bg-teal-600',
      indigo: 'bg-indigo-500 hover:bg-indigo-600',
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      cyan: 'bg-cyan-500 hover:bg-cyan-600',
      lime: 'bg-lime-500 hover:bg-lime-600',
      violet: 'bg-violet-500 hover:bg-violet-600',
    };
    return colors[color] || 'bg-gray-500 hover:bg-gray-600';
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="border-t-4 border-t-purple-500">
        <CardHeader className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
          <div className="flex items-center gap-3">
            <BarChart3 size={32} />
            <div>
              <CardTitle className="text-2xl font-bold">
                Métricas de RH
              </CardTitle>
              <p className="text-white/90 text-sm">
                Análise completa de colaboradores ativos com visualizações interativas
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <div className="flex gap-0">
            {/* Sidebar de Navegação */}
            <div className={`${sidebarOpen ? 'w-64' : 'w-0'} flex-shrink-0 overflow-hidden transition-all duration-300`}>
              <Card className="sticky top-6 w-64">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Categorias</CardTitle>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1 rounded-md hover:bg-white/20 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Recuar sidebar"
                  >
                    <PanelLeftClose size={16} />
                  </button>
                </CardHeader>
                <CardContent className="p-2">
                  <nav className="space-y-1">
                    {menuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                          activeTab === item.id
                            ? `${getColorClasses(item.color)} text-white shadow-lg transform scale-105`
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        <span className="text-xl">{item.icon}</span>
                        <span className="font-medium text-sm">{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>

            {/* Botão expandir (só visível quando recolhida) */}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="self-start mt-2 p-1.5 rounded-md hover:bg-gray-100 text-slate-500 flex-shrink-0 transition-colors"
                title="Expandir sidebar"
              >
                <PanelLeft size={18} />
              </button>
            )}

            {/* Conteúdo Principal */}
            <div className="flex-1 min-w-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>

            <TabsContent value="generos">
              <MetricaGeneros />
            </TabsContent>

            <TabsContent value="regioes">
              <MetricaRegioes />
            </TabsContent>

            <TabsContent value="faixas_etarias">
              <MetricaFaixasEtarias />
            </TabsContent>

            <TabsContent value="hierarquias">
              <MetricaHierarquias />
            </TabsContent>

            <TabsContent value="estado_civil">
              <MetricaEstadoCivil />
            </TabsContent>

            <TabsContent value="deficiencia">
              <MetricaDeficiencia />
            </TabsContent>

            <TabsContent value="dependentes">
              <MetricaDependentes />
            </TabsContent>

            <TabsContent value="tempo_empresa">
              <MetricaTempoEmpresa />
            </TabsContent>

            <TabsContent value="escolaridade">
              <MetricaEscolaridade />
            </TabsContent>

            <TabsContent value="periodo_experiencia">
              <MetricaPeriodoExperiencia />
            </TabsContent>

            <TabsContent value="tipo_contrato">
              <MetricaTipoContrato />
            </TabsContent>

            <TabsContent value="headcount">
              <HeadCountPage />
            </TabsContent>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MetricasPage;
